// Client-side Firebase Authentication module
// Uses Firebase SDK loaded from CDN

import { firebaseConfig, isDevelopment, authEmulatorHost, isFirebaseConfigValid } from '../shared/firebase-config';
import type { AuthUser } from '../shared/types';

// Declare Firebase globals (loaded from CDN)
declare const firebase: {
  initializeApp(config: typeof firebaseConfig): void;
  auth: {
    (): FirebaseAuth;
    GoogleAuthProvider: new () => GoogleAuthProvider;
  };
};

interface FirebaseAuth {
  currentUser: FirebaseUser | null;
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): () => void;
  signInWithPopup(provider: GoogleAuthProvider): Promise<{ user: FirebaseUser }>;
  signOut(): Promise<void>;
  useEmulator(host: string, options?: { disableWarnings?: boolean }): void;
}

interface FirebaseUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  getIdToken(forceRefresh?: boolean): Promise<string>;
}

interface GoogleAuthProvider {
  addScope(scope: string): void;
}

let auth: FirebaseAuth | null = null;
let currentUser: FirebaseUser | null = null;
let authStateCallbacks: ((user: AuthUser | null) => void)[] = [];
let initialized = false;
let authReadyResolve: (() => void) | null = null;
const authReadyPromise = new Promise<void>((resolve) => {
  authReadyResolve = resolve;
});

/**
 * Convert Firebase user to AuthUser
 */
function toAuthUser(user: FirebaseUser | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email || undefined,
    displayName: user.displayName || undefined,
    photoURL: user.photoURL || undefined,
  };
}

/**
 * Initialize Firebase Authentication.
 * Must be called once on page load.
 */
export function initAuth(): boolean {
  if (initialized) {
    console.log('[AUTH] Already initialized');
    return true;
  }

  if (!isFirebaseConfigValid()) {
    console.warn('[AUTH] Firebase config missing - auth disabled');
    console.warn('[AUTH] Set FIREBASE_API_KEY, FIREBASE_AUTH_DOMAIN, and FIREBASE_PROJECT_ID');
    return false;
  }

  if (typeof firebase === 'undefined') {
    console.error('[AUTH] Firebase SDK not loaded - include Firebase scripts in HTML');
    return false;
  }

  try {
    firebase.initializeApp(firebaseConfig);
    auth = firebase.auth();

    // Connect to emulator in development
    if (isDevelopment && authEmulatorHost) {
      auth.useEmulator(`http://${authEmulatorHost}`, { disableWarnings: true });
      console.log(`[AUTH] Using emulator at ${authEmulatorHost}`);
    }

    // Listen for auth state changes
    auth.onAuthStateChanged((user) => {
      currentUser = user;
      const authUser = toAuthUser(user);
      console.log('[AUTH] State changed:', authUser ? authUser.email || authUser.uid : 'signed out');
      authStateCallbacks.forEach(cb => cb(authUser));

      // Resolve authReady promise on first callback (initial state determined)
      if (authReadyResolve) {
        authReadyResolve();
        authReadyResolve = null;
      }
    });

    initialized = true;
    console.log('[AUTH] Firebase initialized successfully');
    return true;
  } catch (error) {
    console.error('[AUTH] Failed to initialize Firebase:', error);
    return false;
  }
}

/**
 * Check if Firebase Auth is initialized
 */
export function isAuthInitialized(): boolean {
  return initialized && auth !== null;
}

/**
 * Returns a promise that resolves when initial auth state is determined.
 * Use this to wait for cached credentials to be restored before connecting.
 */
export function authReady(): Promise<void> {
  return authReadyPromise;
}

/**
 * Subscribe to auth state changes.
 * Returns an unsubscribe function.
 */
export function onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
  authStateCallbacks.push(callback);

  // Immediately call with current state if we have it
  if (initialized) {
    callback(toAuthUser(currentUser));
  }

  // Return unsubscribe function
  return () => {
    authStateCallbacks = authStateCallbacks.filter(cb => cb !== callback);
  };
}

/**
 * Sign in with Google popup.
 * Returns the authenticated user.
 */
export async function signInWithGoogle(): Promise<AuthUser> {
  if (!auth) {
    throw new Error('Firebase Auth not initialized');
  }

  const provider = new firebase.auth.GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');

  const result = await auth.signInWithPopup(provider);
  return toAuthUser(result.user)!;
}

/**
 * Sign out the current user.
 */
export async function signOut(): Promise<void> {
  if (!auth) {
    throw new Error('Firebase Auth not initialized');
  }
  await auth.signOut();
}

/**
 * Get the current user's ID token.
 * Returns null if not signed in.
 */
export async function getIdToken(): Promise<string | null> {
  if (!currentUser) return null;
  return currentUser.getIdToken();
}

/**
 * Get the current authenticated user.
 * Returns null if not signed in.
 */
export function getCurrentUser(): AuthUser | null {
  return toAuthUser(currentUser);
}
